import { createContext, useContext, useState, useCallback } from 'react';

const LS_SELECTED = 'co_taskr_selected_project';
const LS_DEFAULT  = 'co_taskr_default_project';
const LEGACY_ALL_PROJECTS = '__all_projects__';

interface ProjectContextValue {
  selectedProjectId: string | null;
  setSelectedProjectId: (id: string | null) => void;
  defaultProjectId: string | null;
  setDefaultProjectId: (id: string) => void;
}

const ProjectContext = createContext<ProjectContextValue>({
  selectedProjectId: null,
  setSelectedProjectId: () => {},
  defaultProjectId: null,
  setDefaultProjectId: () => {},
});

export function ProjectProvider({ children }: { children: React.ReactNode }) {
  const [defaultProjectId, setDefaultProjectIdState] = useState<string | null>(() => {
    try { return localStorage.getItem(LS_DEFAULT); } catch { return null; }
  });

  const [selectedProjectId, setSelectedProjectIdState] = useState<string | null>(() => {
    try {
      const stored = localStorage.getItem(LS_SELECTED) ?? localStorage.getItem(LS_DEFAULT);
      if (stored === LEGACY_ALL_PROJECTS) return null;
      return stored;
    } catch {
      return null;
    }
  });

  const setSelectedProjectId = useCallback((id: string | null) => {
    setSelectedProjectIdState(id);
    try {
      if (id && id !== LEGACY_ALL_PROJECTS) localStorage.setItem(LS_SELECTED, id);
      else localStorage.removeItem(LS_SELECTED);
    } catch { /* ignore */ }
  }, []);

  const setDefaultProjectId = useCallback((id: string) => {
    setDefaultProjectIdState(id);
    try { localStorage.setItem(LS_DEFAULT, id); } catch { /* ignore */ }
    setSelectedProjectId(id);
  }, [setSelectedProjectId]);

  return (
    <ProjectContext.Provider value={{ selectedProjectId, setSelectedProjectId, defaultProjectId, setDefaultProjectId }}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjectContext() {
  return useContext(ProjectContext);
}
